// Panel único del inspector — grilla densa de paneles de telemetría.
//
// Tres queries con cadencias distintas:
//   live  (sessions/devices/device_codes/entitlements) → 5s
//   full  (todo)                                        → 60s
//   audit (filtros propios)                             → 15s
// El switch RUN/HOLD del shell pausa las tres. Los datos "live" pisan a los
// "full" en las secciones volátiles (merge por sección).

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { logout } from '@/lib/api';
import {
  fetchAudit,
  fetchSnapshot,
  isUnavailable,
  LIVE_SECTIONS,
  type AuditEntry,
  type Device,
  type DeviceCode,
  type EmailToken,
  type Payment,
  type Snapshot,
  type Subscription,
  type TosAcceptance,
  type UserSession,
  type VerificationGrace,
} from '@/lib/api';
import { useI18n, type TFn } from '@/lib/i18n';
import { ActionsPanel } from '@/components/ActionsPanel';
import { GracePanel } from '@/components/GracePanel';
import { WebhookSimPanel } from '@/components/WebhookSimPanel';
import { InspectorShell } from '@/components/InspectorShell';
import { SectionPanel, EmptyHint } from '@/components/SectionPanel';
import { KVGrid } from '@/components/KVGrid';
import { DataTable, type Column } from '@/components/DataTable';
import { StatusBadge, StatusLed, toneForStatus } from '@/components/StatusLed';
import { TimeAgo } from '@/components/TimeAgo';
import { JsonView } from '@/components/JsonView';

const AUDIT_PRESETS = [
  { key: 'audit.w15m', minutes: 15 },
  { key: 'audit.w1h', minutes: 60 },
  { key: 'audit.w24h', minutes: 1440 },
  { key: 'audit.wAll', minutes: 0 },
] as const;

// Ancho de los tres paneles del banco de pruebas. Un tercio en pantallas
// anchas; en tablet caen a mitad y en mobile a ancho completo.
const THIRD = 'col-span-12 md:col-span-6 xl:col-span-4';

function shortId(id: string, keep = 10): string {
  return id.length <= keep ? id : `${id.slice(0, keep)}…`;
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { t, locale } = useI18n();
  const [polling, setPolling] = useState(true);
  const [auditAction, setAuditAction] = useState('');
  const [auditMinutes, setAuditMinutes] = useState<number>(0);

  const fullQ = useQuery({
    queryKey: ['inspector', 'full'],
    queryFn: () => fetchSnapshot(),
    refetchInterval: polling ? 60_000 : false,
  });

  const liveQ = useQuery({
    queryKey: ['inspector', 'live'],
    queryFn: () => fetchSnapshot(LIVE_SECTIONS),
    refetchInterval: polling ? 5_000 : false,
    // Pestaña oculta = nadie mirando: el poll se pausa (regla de tráfico
    // plano en pantallas estacionadas). Al volver al foreground, react-query
    // refetchea solo por refetchOnWindowFocus.
    refetchIntervalInBackground: false,
  });

  const auditFrom = useMemo(
    () => (auditMinutes > 0 ? new Date(Date.now() - auditMinutes * 60_000).toISOString() : undefined),
    // Recalcular el "from" en cada cambio de preset alcanza; no hace falta
    // deslizar la ventana en cada tick (el refetch trae lo nuevo igual).
    [auditMinutes],
  );

  const auditQ = useQuery({
    queryKey: ['inspector', 'audit', auditAction, auditFrom ?? 'all'],
    queryFn: () =>
      fetchAudit({
        ...(auditAction ? { action: auditAction } : {}),
        ...(auditFrom ? { from: auditFrom } : {}),
        limit: 200,
      }),
    refetchInterval: polling ? 15_000 : false,
  });

  // Merge: lo live pisa lo full en las secciones volátiles.
  const snap: Snapshot | undefined = useMemo(() => {
    if (!fullQ.data) return liveQ.data;
    if (!liveQ.data) return fullQ.data;
    return {
      ...fullQ.data,
      sessions: liveQ.data.sessions ?? fullQ.data.sessions,
      devices: liveQ.data.devices ?? fullQ.data.devices,
      device_codes: liveQ.data.device_codes ?? fullQ.data.device_codes,
      entitlements: liveQ.data.entitlements ?? fullQ.data.entitlements,
      verification_grace: liveQ.data.verification_grace ?? fullQ.data.verification_grace,
      session: liveQ.data.session,
    };
  }, [fullQ.data, liveQ.data]);

  const liveAt = liveQ.dataUpdatedAt || undefined;
  const fullAt = fullQ.dataUpdatedAt || undefined;

  // Filas normalizadas (solo cuando la sección está disponible).
  const rows = <T,>(v: T[] | { available: false; error: string } | undefined): T[] =>
    Array.isArray(v) ? v : [];

  const sessions = rows<UserSession>(snap?.sessions);
  const devices = rows<Device>(snap?.devices);
  const deviceCodes = rows<DeviceCode>(snap?.device_codes);
  const subs = rows<Subscription>(snap?.subscriptions);
  const payments = rows<Payment>(snap?.payments);
  const emailTokens = rows<EmailToken>(snap?.email_tokens);
  const tos = snap?.tos && !isUnavailable(snap.tos) ? snap.tos : undefined;
  const tosAcceptances: TosAcceptance[] = tos?.acceptances ?? [];
  const profile = snap?.profile && !isUnavailable(snap.profile) ? snap.profile : undefined;
  const account = snap?.account && !isUnavailable(snap.account) ? snap.account : undefined;
  const ents =
    snap?.entitlements && !isUnavailable(snap.entitlements) ? snap.entitlements : undefined;
  const grace: VerificationGrace | undefined =
    snap?.verification_grace && !isUnavailable(snap.verification_grace)
      ? snap.verification_grace
      : undefined;

  const auditEntries = useMemo(() => auditQ.data?.entries ?? [], [auditQ.data]);
  const knownActions = useMemo(() => {
    const set = new Set<string>(auditEntries.map((e) => e.action));
    if (auditAction) set.add(auditAction);
    return Array.from(set).sort();
  }, [auditEntries, auditAction]);

  const cols = useMemo(() => buildColumns(t, locale), [t, locale]);

  const globalError = fullQ.error ?? liveQ.error ?? auditQ.error;

  return (
    <InspectorShell polling={polling} onTogglePolling={() => setPolling((p) => !p)}>
      {globalError && (
        <p role="alert" className="mb-3 rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-[12px] text-status-error">
          {(globalError as Error).message}
        </p>
      )}

      <div className="grid grid-cols-12 gap-3">
        {/* ── Fila 1: indicadores ─────────────────────────────────────── */}
        <SectionPanel title={t('t.identity')} data={snap?.profile} updatedAt={fullAt} className="col-span-12 md:col-span-6 xl:col-span-3">
          {profile ? (
            <KVGrid
              items={[
                { k: t('kv.userId'), v: profile.user_id },
                { k: t('kv.display'), v: profile.display_name, strong: true },
                { k: t('kv.email'), v: account?.email, strong: true },
                { k: t('kv.status'), v: <StatusBadge status={profile.status} /> },
                {
                  k: t('kv.emailVerif'),
                  v: (
                    <StatusLed
                      tone={profile.email_verified ? 'success' : 'warning'}
                      label={profile.email_verified ? t('val.yes') : t('val.no')}
                    />
                  ),
                },
                { k: t('kv.createdAt'), v: account ? <TimeAgo value={account.created_at} /> : undefined },
              ]}
            />
          ) : (
            <EmptyHint>{t('empty.identity')}</EmptyHint>
          )}
        </SectionPanel>

        <SectionPanel title={t('t.currentSession')} data={snap?.session} updatedAt={liveAt} className="col-span-12 md:col-span-6 xl:col-span-3">
          {snap?.session ? (
            <KVGrid
              items={[
                { k: t('kv.session'), v: shortId(snap.session.session_id, 16), strong: true },
                { k: t('kv.client'), v: snap.session.client_id },
                { k: t('kv.device'), v: snap.session.device_id },
                { k: t('kv.scopes'), v: snap.session.scopes.join(' ') || '—' },
                { k: t('kv.partner'), v: snap.session.partner_id },
              ]}
            />
          ) : (
            <EmptyHint>{t('empty.identity')}</EmptyHint>
          )}
        </SectionPanel>

        <SectionPanel title={t('t.entitlements')} data={snap?.entitlements} updatedAt={liveAt} className="col-span-12 md:col-span-6 xl:col-span-3">
          {ents ? (
            <KVGrid
              items={[
                { k: t('kv.subscription'), v: <StatusBadge status={ents.subscription_status} /> },
                { k: t('kv.plan'), v: ents.plan_display_name ?? ents.plan_code, strong: true },
                { k: t('kv.maxStreams'), v: ents.max_concurrent_streams },
                { k: t('kv.maxDevices'), v: ents.max_devices },
                {
                  k: t('kv.pendingPayment'),
                  v: (
                    <StatusLed
                      tone={ents.has_pending_payment ? 'warning' : 'idle'}
                      label={ents.has_pending_payment ? t('val.yes') : t('val.no')}
                    />
                  ),
                },
              ]}
            />
          ) : (
            <EmptyHint>{t('empty.entitlements')}</EmptyHint>
          )}
        </SectionPanel>

        <SectionPanel title={t('t.tos')} data={snap?.tos} updatedAt={fullAt} className="col-span-12 md:col-span-6 xl:col-span-3">
          {tos ? (
            <KVGrid
              items={[
                { k: t('kv.status'), v: <StatusBadge status={tos.status} /> },
                { k: t('kv.currentVersion'), v: tos.current_version ?? t('kv.noVersions') },
                { k: t('kv.acceptedVersion'), v: tos.accepted_version },
                {
                  k: t('kv.graceUntil'),
                  v: tos.grace_deadline ? <TimeAgo value={tos.grace_deadline} /> : undefined,
                },
              ]}
            />
          ) : (
            <EmptyHint>{t('empty.identity')}</EmptyHint>
          )}
        </SectionPanel>

        {/* ── Fila 2: el banco de pruebas — tres paneles a un tercio ──── */}
        <ActionsPanel
          className={THIRD}
          onExecuted={() => void queryClient.invalidateQueries({ queryKey: ['inspector'] })}
          onSelfRevoked={logout}
        />

        {/* El simulador NO muta la cuenta: manda un webhook real al partner. */}
        <WebhookSimPanel className={THIRD} />

        <GracePanel
          className={THIRD}
          grace={grace}
          updatedAt={liveAt}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ['inspector'] })}
        />

        {/* ── Fila 2: lo volátil (poll 5s) ────────────────────────────── */}
        <SectionPanel title={t('t.sessions')} data={snap?.sessions} updatedAt={liveAt} count={sessions.length} className="col-span-12 xl:col-span-5">
          <DataTable
            rows={sessions}
            rowKey={(s) => s.session_id}
            empty={t('empty.sessions')}
            columns={cols.sessions}
          />
        </SectionPanel>

        <SectionPanel title={t('t.devices')} data={snap?.devices} updatedAt={liveAt} count={devices.length} className="col-span-12 xl:col-span-4">
          {devices.length === 0 && Array.isArray(snap?.devices) ? (
            <EmptyHint>{t('empty.devices')}</EmptyHint>
          ) : (
            <DataTable rows={devices} rowKey={(d) => `${d.partner_id}/${d.device_id}`} empty="" columns={cols.devices} />
          )}
        </SectionPanel>

        <SectionPanel title={t('t.deviceCodes')} data={snap?.device_codes} updatedAt={liveAt} count={deviceCodes.length} className="col-span-12 xl:col-span-3">
          <DataTable
            rows={deviceCodes}
            rowKey={(c) => `${c.client_id}-${c.expires_at}`}
            empty={t('empty.deviceCodes')}
            columns={cols.deviceCodes}
          />
        </SectionPanel>

        {/* ── Fila 3: negocio (poll 60s) ──────────────────────────────── */}
        <SectionPanel title={t('t.subscriptions')} data={snap?.subscriptions} updatedAt={fullAt} count={subs.length} className="col-span-12 xl:col-span-4">
          <DataTable rows={subs} rowKey={(s) => s.subscription_id} empty={t('empty.subscriptions')} columns={cols.subs} />
        </SectionPanel>

        <SectionPanel title={t('t.payments')} data={snap?.payments} updatedAt={fullAt} count={payments.length} className="col-span-12 xl:col-span-3">
          <DataTable rows={payments} rowKey={(p) => p.payment_id} empty={t('empty.payments')} columns={cols.payments} />
        </SectionPanel>

        <SectionPanel title={t('t.emailTokens')} data={snap?.email_tokens} updatedAt={fullAt} count={emailTokens.length} className="col-span-12 xl:col-span-3">
          <DataTable
            rows={emailTokens}
            rowKey={(tk) => `${tk.kind}-${tk.created_at}`}
            empty={t('empty.emailTokens')}
            columns={cols.emailTokens}
          />
        </SectionPanel>

        <SectionPanel title={t('t.tosSignatures')} data={snap?.tos} updatedAt={fullAt} count={tosAcceptances.length} className="col-span-12 xl:col-span-2">
          <DataTable
            rows={tosAcceptances}
            rowKey={(a) => `${a.partner_id}-${a.tos_version}`}
            empty={t('empty.tosSignatures')}
            columns={cols.tosAcceptances}
          />
        </SectionPanel>

        {/* ── Fila 4: audit full-width ────────────────────────────────── */}
        <SectionPanel title={t('t.audit')} data={auditQ.data} updatedAt={auditQ.dataUpdatedAt || undefined} count={auditEntries.length} className="col-span-12">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px]">
            <label htmlFor="audit-action" className="uppercase tracking-wider text-ink-subtle">
              {t('audit.action')}
            </label>
            <select
              id="audit-action"
              value={auditAction}
              onChange={(e) => setAuditAction(e.target.value)}
              className="rounded border border-surface-border-strong bg-surface-muted px-2 py-1 text-ink focus:border-brand focus:outline-none"
            >
              <option value="">{t('val.all')}</option>
              {knownActions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>

            <span className="ml-2 uppercase tracking-wider text-ink-subtle">{t('audit.window')}</span>
            {AUDIT_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setAuditMinutes(p.minutes)}
                aria-pressed={auditMinutes === p.minutes}
                className={`rounded border px-2 py-1 uppercase tracking-wider ${
                  auditMinutes === p.minutes
                    ? 'border-brand text-brand'
                    : 'border-surface-border text-ink-muted hover:border-surface-border-strong'
                }`}
              >
                {t(p.key)}
              </button>
            ))}

            <button
              type="button"
              onClick={() => {
                void fullQ.refetch();
                void liveQ.refetch();
                void auditQ.refetch();
              }}
              className="ml-auto flex items-center gap-1.5 rounded border border-surface-border px-2 py-1 uppercase tracking-wider text-ink-muted hover:border-brand hover:text-brand"
            >
              <RefreshCw className={`h-3 w-3 ${fullQ.isFetching || auditQ.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
              {t('audit.refreshAll')}
            </button>
          </div>

          <DataTable
            rows={auditEntries}
            rowKey={(a) => a.audit_id}
            empty={t('empty.audit')}
            columns={cols.audit}
          />
        </SectionPanel>
      </div>
    </InspectorShell>
  );
}

// ── Definición de columnas (reconstruidas al cambiar idioma) ───────────────

function buildColumns(t: TFn, locale: string) {
  const sessions: Column<UserSession>[] = [
    {
      header: t('col.session'),
      cell: (s) => (
        <span className="flex items-center gap-1.5">
          {shortId(s.session_id, 12)}
          {s.current && (
            <span className="rounded bg-brand/20 px-1 text-[9px] uppercase tracking-wider text-brand">
              {t('val.current')}
            </span>
          )}
        </span>
      ),
    },
    { header: t('col.status'), cell: (s) => <StatusBadge status={s.status} /> },
    { header: t('col.client'), cell: (s) => s.client_id },
    { header: t('col.device'), cell: (s) => shortId(s.device_id, 14) },
    { header: t('col.issued'), cell: (s) => <TimeAgo value={s.issued_at} /> },
    { header: t('col.expires'), cell: (s) => <TimeAgo value={s.expires_at} /> },
  ];

  const devices: Column<Device>[] = [
    { header: t('col.device'), cell: (d) => shortId(d.device_id, 12) },
    { header: t('col.type'), cell: (d) => d.device_type },
    { header: t('col.model'), cell: (d) => d.device_model ?? '—' },
    { header: t('col.partner'), cell: (d) => d.partner_id },
    { header: t('col.login'), cell: (d) => <TimeAgo value={d.login_at} /> },
    { header: t('col.lastSeen'), cell: (d) => <TimeAgo value={d.last_seen_at} /> },
  ];

  const deviceCodes: Column<DeviceCode>[] = [
    { header: t('col.status'), cell: (c) => <StatusBadge status={c.status} /> },
    { header: t('col.name'), cell: (c) => c.device_name ?? '—' },
    { header: t('col.expires'), cell: (c) => <TimeAgo value={c.expires_at} /> },
    { header: t('col.lastPoll'), cell: (c) => <TimeAgo value={c.last_polled_at} /> },
  ];

  const subs: Column<Subscription>[] = [
    { header: t('col.plan'), cell: (s) => s.plan_name ?? s.plan_code ?? s.plan_id },
    { header: t('col.status'), cell: (s) => <StatusBadge status={s.status} /> },
    { header: t('col.partner'), cell: (s) => s.partner_id },
    { header: t('col.provider'), cell: (s) => s.payment_provider },
    { header: t('col.start'), cell: (s) => <TimeAgo value={s.started_at} /> },
    { header: t('col.periodEnd'), cell: (s) => <TimeAgo value={s.current_period_end} /> },
  ];

  const payments: Column<Payment>[] = [
    { header: t('col.date'), cell: (p) => <TimeAgo value={p.created_at} /> },
    {
      header: t('col.amount'),
      className: 'text-right',
      cell: (p) => `${p.amount.toLocaleString(locale)} ${p.currency}`,
    },
    { header: t('col.status'), cell: (p) => <StatusBadge status={p.status} /> },
    { header: t('col.provider'), cell: (p) => p.payment_provider },
    { header: t('col.failure'), cell: (p) => p.failure_code ?? '—' },
  ];

  const emailTokens: Column<EmailToken>[] = [
    { header: t('col.type'), cell: (tk) => tk.kind },
    { header: t('col.created'), cell: (tk) => <TimeAgo value={tk.created_at} /> },
    {
      header: t('col.used'),
      cell: (tk) =>
        tk.used_at ? (
          <StatusLed tone="success" label={t('val.yes')} />
        ) : Date.parse(tk.expires_at) < Date.now() ? (
          <StatusLed tone="error" label={t('val.expired')} />
        ) : (
          <StatusLed tone="warning" label={t('val.no')} />
        ),
    },
  ];

  const tosAcceptances: Column<TosAcceptance>[] = [
    { header: t('col.version'), cell: (a) => a.tos_version },
    { header: t('col.when'), cell: (a) => <TimeAgo value={a.accepted_at} /> },
    { header: t('col.via'), cell: (a) => a.source ?? '—' },
  ];

  const audit: Column<AuditEntry>[] = [
    { header: t('col.when'), cell: (a) => <TimeAgo value={a.occurred_at} /> },
    {
      header: t('col.action'),
      cell: (a) => (
        <span className="flex items-center gap-1.5">
          <StatusLed
            tone={a.action.includes('block') && !a.action.includes('unblock') ? 'error' : toneForStatus('active')}
          />
          {a.action}
        </span>
      ),
    },
    {
      header: t('col.actor'),
      cell: (a) => `${a.actor_type}${a.actor_id ? `:${shortId(a.actor_id, 10)}` : ''}`,
    },
    { header: t('col.reason'), cell: (a) => a.reason ?? '—' },
    { header: t('col.ip'), cell: (a) => a.ip ?? '—' },
    { header: t('col.metadata'), cell: (a) => <JsonView value={a.metadata} label={t('col.view')} /> },
  ];

  return { sessions, devices, deviceCodes, subs, payments, emailTokens, tosAcceptances, audit };
}
